import React, {useMemo, useRef} from 'react'
import {useForm} from 'react-hook-form'
import {Button, Flex, Text, useDisclosure} from '@chakra-ui/react'
import Field from '../../../components/field'
import WarrantyFooter from './warranty-footer'
import WarrantyHeader from './warranty-header'
import {useWarrantyContext, WARRANTY_MODES} from '../util/warranty-context'
import WarrantyFormContainer from './warranty-form-container'
import WarrantyStepContainer from './warranty-step-container'
import {FormattedMessage, useIntl} from 'react-intl'
import {GenericModal} from '../../../hooks/use-generic-modal'
import ContentAsset from '../../../components/content-asset'
import {GENERAL_FORM_VALIDATION_MESSAGES} from '../../../constants'

const ProductDetails = () => {
    const {formatMessage} = useIntl()
    const {
        config,
        data: {
            userInputSelectedProductName,
            userInputSelectedProductGroup,
            serialNumber,
            purchaseDate,
            userInputItemNumber,
            manufactureDate
        } = {},
        mode,
        onSubmit
    } = useWarrantyContext()
    const disclosure = useDisclosure()
    const serialNumberInfoRef = useRef(null)

    const form = useForm({
        defaultValues: {
            serialNumber,
            purchaseDate,
            userInputItemNumber,
            manufactureDate
        }
    })

    // Uncomment the following line if you want to use the custom mutation for checking serial numbers
    // const {mutateAsync: checkSerialNumber} = useCustomWarrantyCheckSerialNumberMutation()

    const selectedItemConfig = useMemo(
        () => config?.warranties.find((item) => item.name === userInputSelectedProductName),
        [config, userInputSelectedProductName]
    )

    if (process.env.NODE_ENV !== 'production') {
        if (!selectedItemConfig) {
            throw new Error(
                `Selected product ${userInputSelectedProductName} is not found in the configuration. Available products: ${config?.warranties
                    .map((item) => item.name)
                    .join(', ')}`
            )
        } else if (selectedItemConfig.group !== userInputSelectedProductGroup) {
            throw new Error(
                `Selected item group "${userInputSelectedProductGroup}" does not match the configuration group "${selectedItemConfig.group}".`
            )
        }
    }

    const isNomiGroup = useMemo(
        () => userInputSelectedProductGroup?.toLowerCase()?.indexOf('nomi') >= 0,
        [userInputSelectedProductGroup]
    )
    const helpAssetId = selectedItemConfig?.helpAsset

    const fields = {
        serialNumber: {
            name: 'serialNumber',
            label: formatMessage({
                defaultMessage: 'Enter your serial number',
                id: 'warranty_portal.input.label.serial_number'
            }),
            type: 'text',
            inputProps: {
                autoComplete: 'off',
                isRequired: mode === WARRANTY_MODES.WARRANTY && !isNomiGroup
            },
            rules: {
                ...(mode === WARRANTY_MODES.WARRANTY &&
                    !isNomiGroup && {
                        required: formatMessage({
                            defaultMessage: 'This field is required.',
                            id: 'global.error.field_is_required'
                        })
                    }),
                ...(selectedItemConfig?.regex && {
                    pattern: {
                        value: new RegExp(selectedItemConfig.regex),
                        message: formatMessage({
                            defaultMessage: 'Invalid serial number format.',
                            id: 'warranty_portal.input.error.invalid_serial_number_format'
                        })
                    }
                })
                // Temporary commented out the async validation for serial number as the API always returns failure
                // validate: async (value) => {
                //     if (!value) {
                //         return true
                //     }
                //     try {
                //         const res = await checkSerialNumber({parameters: {c_number: value}})
                //         if (res?.isSuccess) {
                //             return true
                //         }
                //     } catch (error) {
                //         logger.error('Error checking serial number', error)
                //         toast({
                //             title: formatMessage(API_ERROR_MESSAGE),
                //             status: 'error'
                //         })
                //     }
                //
                //     return formatMessage({
                //         defaultMessage: 'Invalid serial number.',
                //         id: 'warranty_portal.input.error.invalid_serial_number'
                //     })
                // }
            },
            control: form.control,
            error: form.formState.errors.serialNumber
        },
        purchaseDate: {
            name: 'purchaseDate',
            label: formatMessage({
                defaultMessage: 'Purchase date',
                id: 'warranty_portal.input.label.purchase_date'
            }),
            type: 'date',
            inputProps: {
                isRequired: true,
                autocomplete: 'off'
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'global.error.field_is_required'
                })
            },
            control: form.control,
            error: form.formState.errors.purchaseDate
        },
        ...(mode === WARRANTY_MODES.CPSIA && {
            itemNumber: {
                name: 'userInputItemNumber',
                label: formatMessage({
                    defaultMessage: 'Item number',
                    id: 'warranty.label.item_number'
                }),
                type: 'text',
                inputProps: {
                    isRequired: true,
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    autocomplete: 'off'
                },
                rules: {
                    required: formatMessage({
                        defaultMessage: 'This field is required.',
                        id: 'global.error.field_is_required'
                    }),
                    pattern: {
                        value: /^\d{6}$/,
                        message: formatMessage({
                            defaultMessage: 'Please enter a valid number.',
                            id: 'validate.number'
                        })
                    },
                    minLength: {
                        message: formatMessage(GENERAL_FORM_VALIDATION_MESSAGES.minLength, {
                            label: formatMessage({
                                defaultMessage: 'Item number',
                                id: 'warranty.label.item_number'
                            }),
                            n: 6
                        }),
                        value: 6
                    },
                    maxLength: {
                        message: formatMessage(GENERAL_FORM_VALIDATION_MESSAGES.maxLength, {
                            label: formatMessage({
                                defaultMessage: 'Item number',
                                id: 'warranty.label.item_number'
                            }),
                            n: 6
                        }),
                        value: 6
                    }
                },
                control: form.control,
                error: form.formState.errors.userInputItemNumber
            },
            manufactureDate: {
                name: 'manufactureDate',
                label: formatMessage({
                    defaultMessage: 'Manufacture date',
                    id: 'warranty.label.manufacture_date'
                }),
                type: 'date',
                inputProps: {
                    autocomplete: 'off',
                    isRequired: true
                },
                rules: {
                    required: formatMessage({
                        defaultMessage: 'This field is required.',
                        id: 'global.error.field_is_required'
                    })
                },
                control: form.control,
                error: form.formState.errors.manufactureDate
            }
        })
    }

    return (
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <WarrantyStepContainer>
                <WarrantyHeader>
                    <FormattedMessage
                        defaultMessage="Enter your product details"
                        id="warranty_portal.product_details.header"
                    />
                </WarrantyHeader>

                <Text>{userInputSelectedProductName}</Text>

                <WarrantyFormContainer>
                    <Flex direction="column" alignItems="stretch" gap={4}>
                        <Field {...fields.serialNumber} />
                        {helpAssetId ? (
                            <Button
                                ref={serialNumberInfoRef}
                                alignSelf="flex-start"
                                variant="link"
                                onClick={disclosure.onOpen}
                            >
                                <FormattedMessage
                                    id="warranty.productdetails.serialnumberhow"
                                    defaultMessage="How do I find the serial number?"
                                />
                            </Button>
                        ) : null}
                    </Flex>
                    <Field {...fields.purchaseDate} />
                    {mode === WARRANTY_MODES.CPSIA && (
                        <>
                            <Field {...fields.itemNumber} />
                            <Field {...fields.manufactureDate} />
                        </>
                    )}
                </WarrantyFormContainer>

                <WarrantyFooter />
            </WarrantyStepContainer>
            <GenericModal
                {...disclosure}
                finalFocusRef={serialNumberInfoRef}
                header={
                    <Text>
                        <FormattedMessage
                            id="warranty.productdetails.serialnumberhow"
                            defaultMessage="How do I find the serial number?"
                        />
                    </Text>
                }
            >
                <ContentAsset id={helpAssetId} />
            </GenericModal>
        </form>
    )
}

export default ProductDetails
